import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

// Firebase configuration — pulled from environment so the values can be
// rotated without code changes. Firebase web "API key" is a public client
// identifier (NOT a secret), but we still externalise it so deployments
// can swap projects without rebuilding the bundle.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// VAPID Key for Web Push
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Initialize Firebase
let app;
let messaging;

try {
  if (!firebaseConfig.apiKey) {
    console.warn('Firebase config missing; push notifications disabled.');
  } else {
    app = initializeApp(firebaseConfig);
    // Only initialize messaging if browser supports it
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window) {
      messaging = getMessaging(app);
    }
  }
} catch (error) {
  console.error('Firebase initialization error:', error);
}

/**
 * Check if running as iOS PWA (added to Home Screen)
 */
export const isIOSPWA = () => {
  return (
    typeof window !== 'undefined' &&
    window.navigator.standalone === true // iOS Safari standalone mode
  );
};

/**
 * Check if iOS Safari (not PWA)
 */
export const isIOSSafari = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
  return isIOS && isSafari && !isIOSPWA();
};

/**
 * Check if macOS Safari
 */
export const isMacSafari = () => {
  const ua = navigator.userAgent;
  const isMac = /Macintosh/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
  return isMac && isSafari;
};

/**
 * Request notification permission and get FCM token
 * @returns {Promise<string|null>} FCM token or null if permission denied
 */
export const requestNotificationPermission = async () => {
  // Check basic support
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('Push notifications not supported');
    return null;
  }

  try {
    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }

    // Register service worker
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log('Service worker registered:', registration);

    // For Safari/iOS PWA, use standard Web Push API
    if (isIOSPWA() || isMacSafari()) {
      try {
        // Try standard Web Push subscription
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_KEY)
        });
        
        // Convert subscription to a token-like string for backend storage
        const token = JSON.stringify(subscription.toJSON());
        console.log('Web Push subscription obtained');
        return token;
      } catch (webPushError) {
        console.log('Web Push failed, trying Firebase:', webPushError);
      }
    }

    // For Chrome/Firefox/Edge, use Firebase
    if (messaging) {
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration
      });

      if (token) {
        console.log('FCM Token obtained:', token.substring(0, 20) + '...');
        return token;
      }
    }

    console.log('No token available');
    return null;
  } catch (error) {
    console.error('Error getting notification permission:', error);
    return null;
  }
};

/**
 * Convert VAPID key from base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Set up foreground message handler
 * @param {Function} callback - Function to call when message received
 * @returns {Function} Unsubscribe function
 */
export const onForegroundMessage = (callback) => {
  if (!messaging) {
    console.warn('Firebase messaging not supported');
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
};

/**
 * Check if notifications are supported
 * @returns {boolean}
 */
export const isNotificationSupported = () => {
  if (typeof window === 'undefined') {
    console.log('isNotificationSupported: window undefined');
    return false;
  }
  if (!('serviceWorker' in navigator)) {
    console.log('isNotificationSupported: no serviceWorker');
    return false;
  }
  if (!('PushManager' in window)) {
    console.log('isNotificationSupported: no PushManager');
    return false;
  }
  if (!('Notification' in window)) {
    console.log('isNotificationSupported: no Notification API');
    return false;
  }
  // Check secure context (HTTPS required)
  if (!window.isSecureContext) {
    console.log('isNotificationSupported: not secure context (HTTPS required)');
    return false;
  }
  console.log('isNotificationSupported: true');
  return true;
};

/**
 * Get current notification permission status
 * @returns {string} 'granted', 'denied', or 'default'
 */
export const getNotificationPermissionStatus = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
};

/**
 * Get platform info for UI display
 */
export const getPlatformInfo = () => {
  // Check if running in browser
  if (typeof window === 'undefined') {
    return {
      platform: 'unsupported',
      supported: false,
      message: 'Browser environment not detected'
    };
  }

  // iOS Safari (not PWA) - needs to be added to Home Screen
  if (isIOSSafari()) {
    return {
      platform: 'ios_safari',
      supported: false,
      message: 'Add this app to your Home Screen to enable notifications',
      instructions: [
        'Tap the Share button (square with arrow)',
        'Scroll down and tap "Add to Home Screen"',
        'Tap "Add" in the top right',
        'Open the app from your Home Screen',
        'Enable notifications in Settings'
      ]
    };
  }
  
  // iOS PWA (added to Home Screen)
  if (isIOSPWA()) {
    return {
      platform: 'ios_pwa',
      supported: true,
      message: 'Push notifications available'
    };
  }
  
  // macOS Safari
  if (isMacSafari()) {
    return {
      platform: 'mac_safari',
      supported: true,
      message: 'Push notifications available in Safari'
    };
  }
  
  // Standard browsers (Chrome, Firefox, Edge, etc.)
  if (isNotificationSupported()) {
    return {
      platform: 'standard',
      supported: true,
      message: 'Push notifications available'
    };
  }
  
  // Fallback - unsupported
  return {
    platform: 'unsupported',
    supported: false,
    message: 'Your browser does not support push notifications'
  };
};

export { app, messaging };
